<app-page-shell>
  <app-page-hero [auditStamp]="{
    createdAt: plan?.createdAt,
    updatedAt: plan?.updatedAt,
    createdBy: plan?.createdByCustomerId,
    createdByActor: plan?.createdByActor
  }"
    eyebrow="Transport Planning"
    [title]="plan?.planNumber || 'Truck Plan'"
    [subtitle]="plan?.planName || 'Review the truck routes before creating shipments.'">
    <div actions>
      @if (isBulkPlan()) {
        <p-button label="Back To Bulk Run" icon="pi pi-arrow-left" severity="secondary" [outlined]="true"
          [routerLink]="['/transport-planning/bulk-planning', plan?.bulkPlanningRunId]"></p-button>
      } @else {
        <p-button label="Back To Plans" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" routerLink="/transport-planning/plans"></p-button>
        <p-button label="Arrange Trucks" icon="fa-solid fa-wand-magic-sparkles" [loading]="isLoading" [disabled]="isPlanReadOnly() || plan?.stale" (onClick)="optimize()"></p-button>
        <p-button label="Manual Match" icon="pi pi-user-edit" severity="secondary" [outlined]="true" [disabled]="isPlanReadOnly() || plan?.stale" (onClick)="scrollToManualWorkbench()"></p-button>
        <p-button label="Create Shipments" icon="pi pi-check" severity="success" [loading]="isLoading" [disabled]="plan?.status !== 'SOLVED' || plan?.stale" (onClick)="apply()"></p-button>
        @if (canCancelPlan()) {
          <p-button label="Cancel Plan" icon="pi pi-ban" severity="danger" [outlined]="true" [disabled]="isLoading" (onClick)="openCancelDialog()"></p-button>
        }
      }
    </div>
  </app-page-hero>

  @if (message) {
    <div class="notice"><i class="pi pi-info-circle"></i><span>{{ message }}</span></div>
  }
  @if (isBulkPlan()) {
    <div class="notice"><i class="pi pi-info-circle"></i><span>Bulk scenarios are read-only here. Use the Bulk Planning workbench for adjustments, validation, and approval.</span></div>
  }

  <section class="plan-state" [class.warning-state]="issueTitle()" [class.success-state]="plan?.status === 'APPLIED'">
    <div class="state-icon"><i [class]="stateIcon()"></i></div>
    <div class="state-copy">
      <h2>{{ stateTitle() }}</h2>
      <p>{{ stateMessage() }}</p>
      @if (plan?.status === 'CANCELLED') {
        <div class="cancellation-audit">
          <strong>Cancelled {{ plan?.cancelledAt | date:'medium' }} by {{ plan?.cancelledByActor?.name || ('Customer #' + plan?.cancelledByCustomerId) }}</strong>
          <span>Reason: {{ plan?.cancellationReason }}</span>
        </div>
      }
      @if (shipmentLinks().length > 0) {
        <div class="state-shipment-links" aria-label="Created shipments">
          @for (link of shipmentLinks(); track link.executionLinkId) {
            <a [routerLink]="['/shipment/view', link.shipmentId]">
              <i class="pi pi-truck"></i>
              <span>Open {{ shipmentLinkLabel(link) }}</span>
              <i class="pi pi-arrow-right"></i>
            </a>
          }
        </div>
      }
    </div>
    <p-tag [value]="plan?.status || 'DRAFT'" [severity]="statusSeverity(plan?.status)"></p-tag>
  </section>

  <app-page-data-panel title="At a glance" subtitle="Current truck plan readiness.">
    <div class="summary-grid">
      <div><i class="pi pi-calendar-plus"></i><span>Starts</span><strong>{{ date(plan?.horizonStart) }}</strong></div>
      <div><i class="pi pi-calendar-clock"></i><span>Ends</span><strong>{{ date(plan?.horizonEnd) }}</strong></div>
      <div><i class="pi pi-map"></i><span>Routes</span><strong>{{ plan?.routes?.length || 0 }}</strong></div>
      <div><i class="pi pi-truck"></i><span>Trucks checked</span><strong>{{ plan?.vehicles?.length || 0 }}</strong></div>
    </div>
    @if (issueTitle()) {
      <div class="review-note">
        <i class="pi pi-exclamation-triangle"></i>
        <div>
          <strong>{{ issueTitle() }}</strong>
          <span>{{ issueMessage() }}</span>
          @if (technicalDetails()) {
            <details>
              <summary>Technical details</summary>
              <pre>{{ technicalDetails() }}</pre>
            </details>
          }
        </div>
      </div>
    }
  </app-page-data-panel>


  <app-page-data-panel title="Trucks available" subtitle="Ready fleet capacity for this planning window.">
    <p-table [value]="plan?.vehicles || []" responsiveLayout="scroll" styleClass="workspace-table">
      <ng-template pTemplate="header">
        <tr>
          <th>Truck</th>
          <th>Driver</th>
          <th>Available</th>
          <th>Rental cost</th>
          <th>Status</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr>
          <td>
            <strong>{{ row.vehiclePlateNumber || row.vehicleId }}</strong>
            <small>{{ row.vehicleTypeName || 'Vehicle type not set' }}</small>
            <small>{{ resourceOwnerLine(row.vehicleContext) }}</small>
            <small>{{ resourceAgencySourceLine(row.vehicleContext) }}</small>
          </td>
          <td>
            <strong>{{ row.driverName || 'Driver not set' }}</strong>
            <small>{{ resourceOwnerLine(row.driverContext) }}</small>
            <small>{{ resourceAgencySourceLine(row.driverContext) }}</small>
          </td>
          <td>{{ date(row.availabilityStart) }} - {{ date(row.availabilityEnd) }}</td>
          <td>{{ rentalCostLabel(row) }}<small *ngIf="rentalCostPreviews[row.vehicleSnapshotId || 0]?.uncoveredDays">{{ rentalCostPreviews[row.vehicleSnapshotId || 0]?.uncoveredDays }} uncovered days</small></td>
          <td><p-tag [value]="row.validationStatus || 'READY'" [severity]="row.validationStatus === 'ERROR' ? 'danger' : 'success'"></p-tag></td>
        </tr>
      </ng-template>
    </p-table>
  </app-page-data-panel>

  @if (!isBulkPlan() && plan?.status !== 'CANCELLED') {
  <div id="manual-workbench">
    <app-page-data-panel
      title="Manual load and truck matching"
      subtitle="Assign each load to a truck when you want to plan without Google optimization."
      [resultText]="manualProgressText()">
      <div actions>
        <p-button
          label="Save Manual Match"
          icon="pi pi-save"
          severity="success"
          [loading]="isLoading"
          [disabled]="!canSaveManualMatches() || isLoading"
          (onClick)="saveManualMatches()"></p-button>
      </div>

      @if ((plan?.releases?.length || 0) === 0) {
        <div class="empty"><i class="pi pi-box"></i><span>No loads are attached to this plan.</span></div>
      } @else if (manualLanes.length === 0) {
        <div class="empty"><i class="pi pi-truck"></i><span>No ready trucks are available for manual matching.</span></div>
      } @else {
        <div class="manual-steps">
          <div><i class="pi pi-box"></i><strong>Choose load</strong><span>{{ unmatchedReleases().length }} unmatched</span></div>
          <div><i class="pi pi-truck"></i><strong>Choose truck</strong><span>{{ manualLanes.length }} ready</span></div>
          <div><i class="pi pi-save"></i><strong>Save match</strong><span>Create reviewable routes</span></div>
        </div>

        <div class="manual-picker">
          <label>
            <span class="field-label"><span>1</span> Load to move</span>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-box"></i></p-inputgroup-addon>
              <p-select
                inputId="manual-load"
                [options]="manualLoadOptions()"
                [(ngModel)]="manualReleaseId"
                optionLabel="label"
                optionValue="value"
                placeholder="Choose unmatched load"
                [filter]="true"
                styleClass="manual-select"></p-select>
            </p-inputgroup>
          </label>

          <label>
            <span class="field-label"><span>2</span> Truck to use</span>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-truck"></i></p-inputgroup-addon>
              <p-select
                inputId="manual-truck"
                [options]="manualTruckOptions()"
                [(ngModel)]="manualTruckId"
                optionLabel="label"
                optionValue="value"
                placeholder="Choose truck"
                [filter]="true"
                styleClass="manual-select"></p-select>
            </p-inputgroup>
          </label>

          <p-button
            label="Assign Load"
            icon="pi pi-arrow-right"
            [disabled]="!manualReleaseId || !manualTruckId || isPlanReadOnly()"
            (onClick)="assignManualLoad()"></p-button>
        </div>

        <div class="unmatched-strip" [class.ready]="unmatchedReleases().length === 0">
          <i [class]="unmatchedReleases().length === 0 ? 'pi pi-check-circle' : 'pi pi-exclamation-circle'"></i>
          <div>
            <strong>{{ unmatchedReleases().length === 0 ? 'All loads are matched' : unmatchedReleases().length + ' loads still need a truck' }}</strong>
            <span>{{ unmatchedReleases().length === 0 ? 'You can save the manual match now.' : 'Pick the next load from the dropdown above.' }}</span>
          </div>
        </div>

        <div class="manual-lanes">
          @for (lane of manualLanes; track lane.vehicleSnapshotId) {
            <section class="manual-lane">
              <div class="lane-head">
                <div class="lane-icon"><i class="pi pi-truck"></i></div>
                <div>
                  <h3>{{ lane.vehicleLabel }}</h3>
                  <p>{{ assignedReleases(lane).length }} load{{ assignedReleases(lane).length === 1 ? '' : 's' }}</p>
                  @if (laneVehicle(lane); as laneVehicle) {
                    <small>{{ resourceOwnerLine(laneVehicle.vehicleContext) }}</small>
                    <small>{{ resourceAgencySourceLine(laneVehicle.vehicleContext) }}</small>
                    <small>{{ resourceOwnerLine(laneVehicle.driverContext) }}</small>
                  }
                </div>
                @if (assignedReleases(lane).length > 0 && !isPlanReadOnly()) {
                  <button type="button" class="icon-button" (click)="clearLane(lane)" aria-label="Clear truck assignments">
                    <i class="pi pi-times"></i>
                  </button>
                }
              </div>

              <div class="lane-loads">
                @if (assignedReleases(lane).length === 0) {
                  <div class="lane-empty"><i class="pi pi-inbox"></i><span>No loads assigned.</span></div>
                }
                @for (release of assignedReleases(lane); track release.releaseId) {
                  <article class="load-card">
                    <div>
                      <strong>{{ releaseLabel(release) }}</strong>
                      <em>{{ customerLabel(release) }}{{ accountLabel(release) ? ' / ' + accountLabel(release) : '' }}</em>
                      <span>{{ releaseRoute(release) }}</span>
                      <small>{{ releaseLoad(release) }}</small>
                    </div>
                    @if (!isPlanReadOnly()) {
                      <button type="button" class="icon-button" (click)="removeManualLoad(lane, release.releaseId)" aria-label="Remove load">
                        <i class="pi pi-times"></i>
                      </button>
                    }
                  </article>
                }
              </div>
            </section>
          }
        </div>
      }
    </app-page-data-panel>
  </div>
  }

  <app-page-data-panel title="Suggested routes" subtitle="Review the stop order before creating shipments.">
    @if ((plan?.routes?.length || 0) === 0) {
      <div class="empty"><i class="pi pi-map"></i><span>No routes yet. Click Arrange Trucks.</span></div>
    }
    @for (route of plan?.routes || []; track route.routeId) {
      <section class="route-card">
        <div class="route-head">
          <div>
            <h3><i class="pi pi-map-marker"></i> Route {{ route.routeSequence }}</h3>
            <p>{{ route.vehicleLabel || 'Truck not assigned' }}</p>
            <small>{{ resourceOwnerLine(route.vehicleContext) }}</small>
            <small>{{ resourceAgencySourceLine(route.vehicleContext) }}</small>
            <small>{{ resourceOwnerLine(route.driverContext) }}</small>
            <small>{{ resourceAgencySourceLine(route.driverContext) }}</small>
          </div>
          <div class="route-metrics">
            <span>{{ routeDisplayStops(route).length }} physical stops</span>
            <span>{{ routeReleaseCount(route) }} releases</span>
            <span>Billing: {{ route.billingMode === 'CONSOLIDATED_ZERO_SELL' ? 'Shared · zero sell' : route.billingMode === 'CONSOLIDATED_PER_PARTY' ? 'Shared · per party' : 'Standard' }}</span>
            <span>{{ distance(route) }}</span>
            <span>{{ duration(route.totalDurationSeconds) }}</span>
          </div>
        </div>
        @if (routeRawStopCount(route) > routeDisplayStops(route).length) {
          <div class="route-merge-note">
            <i class="pi pi-clone"></i>
            <span>{{ routeRawStopCount(route) }} release stop records consolidated into {{ routeDisplayStops(route).length }} physical route stops.</span>
          </div>
        }
        <div class="route-pricing">
          <div>
            <strong>Shipment pricing</strong>
            <span>{{ mixedRouteNotice(route) || routePricingHint(route) }}</span>
          </div>
          <div class="route-pricing-fields">
            <div class="route-price-field">
              <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-arrow-down"></i></p-inputgroup-addon>
                <p-iftalabel>
                  <p-inputnumber
                    [inputId]="'route-buying-' + route.routeId"
                    [(ngModel)]="route.buyingPrice"
                    mode="decimal"
                    [min]="0"
                    [minFractionDigits]="2"
                    [maxFractionDigits]="2"
                    [disabled]="isPlanReadOnly()">
                  </p-inputnumber>
                  <label [for]="'route-buying-' + route.routeId">{{ routeOperationalCostLabel(route) }}</label>
                </p-iftalabel>
              </p-inputgroup>
              <div class="route-price-source">
                <p-tag [value]="routeBuyingSourceLabel(route)" [severity]="routeBuyingSourceSeverity(route)"></p-tag>
                @if (routeBuyingSourceDetail(route)) {
                  <small>{{ routeBuyingSourceDetail(route) }}</small>
                }
              </div>
            </div>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-arrow-up"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-inputnumber
                  [inputId]="'route-selling-' + route.routeId"
                  [(ngModel)]="route.sellingPrice"
                  mode="decimal"
                  [min]="0"
                  [minFractionDigits]="2"
                  [maxFractionDigits]="2"
                  [disabled]="isPlanReadOnly() || route.billingMode === 'CONSOLIDATED_ZERO_SELL'">
                </p-inputnumber>
                <label [for]="'route-selling-' + route.routeId">Selling price</label>
              </p-iftalabel>
            </p-inputgroup>
            <span class="route-margin">{{ routeMargin(route) }}</span>
          </div>
        </div>
        @if ((route.projectAllocations?.length || 0) > 0) {
          <section class="project-allocation-panel">
            <div class="project-allocation-head">
              <div>
                <strong><i class="pi pi-percentage"></i> Project allocation</strong>
                <span>One audited percentage controls planning cost, transport cost, buying, and selling.</span>
              </div>
              <div class="project-allocation-actions">
                <p-tag
                  [value]="(allocationTotal(route) | number:'1.2-6') + '% allocated'"
                  [severity]="allocationTotalValid(route) ? 'success' : 'danger'">
                </p-tag>
                <p-button
                  label="Reset calculated allocation"
                  icon="pi pi-refresh"
                  severity="secondary"
                  [outlined]="true"
                  size="small"
                  [disabled]="isPlanReadOnly() || isLoading"
                  (onClick)="resetProjectAllocations(route)">
                </p-button>
              </div>
            </div>
            <div class="project-allocation-table">
              <div class="project-allocation-row project-allocation-labels">
                <span>Logistics project</span>
                <span>Allocation</span>
                <span>Planning cost</span>
                <span>{{ routeOperationalCostLabel(route) }}</span>
                <span>Selling</span>
              </div>
              @for (allocation of route.projectAllocations; track allocation.logisticsProjectId) {
                <div class="project-allocation-row">
                  <div class="project-allocation-project">
                    <strong>{{ allocation.projectNumber || ('Project #' + allocation.logisticsProjectId) }}</strong>
                    <span>{{ allocation.projectName || 'Logistics project' }}</span>
                    <small>
                      {{ allocation.releaseCount || 0 }} releases · {{ allocation.allocationBasisType || 'EQUAL' }}
                      @if (allocation.manualOverride) { · Manual }
                    </small>
                  </div>
                  <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-percentage"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <p-inputnumber
                        [inputId]="'project-allocation-' + route.routeId + '-' + allocation.logisticsProjectId"
                        [(ngModel)]="allocation.allocationPercentage"
                        mode="decimal"
                        [min]="0.000001"
                        [max]="100"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="6"
                        [disabled]="isPlanReadOnly()"
                        (ngModelChange)="allocation.manualOverride = true">
                      </p-inputnumber>
                      <label [for]="'project-allocation-' + route.routeId + '-' + allocation.logisticsProjectId">Percentage</label>
                    </p-iftalabel>
                  </p-inputgroup>
                  <strong class="allocation-money">{{ allocatedMoney(allocation.allocatedPlanningCost) }}</strong>
                  <strong class="allocation-money">{{ allocatedMoney(projectOperationalAllocation(route, allocation)) }}</strong>
                  <strong class="allocation-money">{{ allocatedMoney(allocation.allocatedSelling) }}</strong>
                </div>
              }
            </div>
            @if (!allocationTotalValid(route)) {
              <div class="project-allocation-error">
                <i class="pi pi-exclamation-triangle"></i>
                Allocation must total exactly 100% before pricing can be saved or shipments created.
              </div>
            }
          </section>
        }
        <div class="stop-list">
          @for (stop of routeDisplayStops(route); track stop.displaySequence + '-' + stop.stopType + '-' + stop.locationName) {
            <div class="stop-row">
              <div class="stop-marker">{{ stop.displaySequence }}</div>
              <div class="stop-content">
                <div class="stop-main">
                  <div class="stop-title">
                    <strong>{{ stop.stopType }} - {{ stop.locationName }}</strong>
                    @if (stop.rawStopCount > 1) {
                      <p-tag [value]="stop.rawStopCount + ' release stops'" severity="info"></p-tag>
                    }
                  </div>
                  <span>
                    Arrival {{ date(stop.plannedArrival) }}
                    @if (stop.plannedDeparture) {
                      <span> | Depart {{ date(stop.plannedDeparture) }}</span>
                    }
                  </span>
                  <span class="stop-segment"><i class="pi pi-route"></i>{{ routeStopSegment(route, stop) }}</span>
                  @if (routeStopCustomers(stop)) {
                    <small [class.missing-customer]="routeStopNeedsCustomer(stop)">{{ routeStopCustomers(stop) }}</small>
                  }
                  @if (routeStopLoadSummary(stop)) {
                    <em>{{ routeStopLoadSummary(stop) }}</em>
                  }
                </div>
                <aside class="stop-side">
                  <div class="stop-actions">
                    @if (routeStopMapUrl(stop)) {
                      <a class="stop-action-button" [href]="routeStopMapUrl(stop)" target="_blank" rel="noopener">
                        <i class="pi pi-map-marker"></i>
                        <span>Open Maps</span>
                      </a>
                    }
                    @if (routeStopNeedsCustomer(stop) && !isPlanReadOnly()) {
                      <button type="button" class="stop-action-button warn" (click)="openDefaultContactDialog(stop)">
                        <i class="pi pi-user-plus"></i>
                        <span>Set Default Contact</span>
                      </button>
                    }
                  </div>
                  <div class="stop-release-panel">
                    <strong>Order releases</strong>
                    @for (release of routeStopReleases(stop); track release.releaseId) {
                      <a [routerLink]="['/transport-planning/releases', release.releaseId]">
                        <span>{{ releaseLabel(release) }}</span>
                        <small>{{ releaseLoad(release) }}</small>
                        <small><i class="pi pi-users"></i> Assign field resources</small>
                      </a>
                    }
                  </div>
                </aside>
              </div>
            </div>
          }
        </div>
      </section>
    }
    @if (!isBulkPlan() && plan?.status === 'SOLVED' && (plan?.routes?.length || 0) > 0) {
      <div class="route-pricing-actions">
        <p-button label="Save Route Pricing" icon="pi pi-save" severity="secondary" [loading]="isLoading" (onClick)="saveRoutePricing()"></p-button>
      </div>
    }
  </app-page-data-panel>

  <p-dialog
    header="Set Location Default Contact"
    [(visible)]="contactDialogVisible"
    [modal]="true"
    [style]="{ width: '42rem', maxWidth: '95vw' }"
    [draggable]="false">
    <div class="contact-dialog">
      <div class="contact-dialog-head">
        <strong>{{ contactSelectorStop?.locationName || 'Location' }}</strong>
        <span>Search and select the contact that should be used as the location default sender/receiver contact.</span>
      </div>
      <p-inputgroup>
        <p-inputgroup-addon><i class="pi pi-search"></i></p-inputgroup-addon>
        <input
          pInputText
          type="text"
          [(ngModel)]="contactSearchQuery"
          (keyup.enter)="searchRouteContacts()"
          placeholder="Search contact by name, phone, or email" />
        <p-button label="Search" icon="pi pi-search" [loading]="contactSearchLoading" (onClick)="searchRouteContacts()"></p-button>
      </p-inputgroup>
      <div class="contact-results">
        @if (contactSearchLoading) {
          <div class="contact-empty">Searching contacts...</div>
        } @else if (contactSearchResults.length === 0) {
          <div class="contact-empty">Search for a contact to assign as this location's default contact.</div>
        }
        @for (contact of contactSearchResults; track contact.id) {
          <button
            type="button"
            class="contact-result"
            [class.selected]="selectedDefaultContact?.id === contact.id"
            (click)="selectDefaultContact(contact)">
            <strong>{{ contact.name || ('Contact #' + contact.id) }}</strong>
            <span>{{ contact.phoneNumber || 'No phone' }}{{ contact.emailAddress ? ' | ' + contact.emailAddress : '' }}</span>
          </button>
        }
      </div>
    </div>
    <ng-template pTemplate="footer">
      <p-button label="Cancel" severity="secondary" [outlined]="true" (onClick)="contactDialogVisible = false"></p-button>
      <p-button label="Save Default Contact" icon="pi pi-save" [disabled]="!selectedDefaultContact" [loading]="isLoading" (onClick)="saveDefaultContact()"></p-button>
    </ng-template>
  </p-dialog>
  <p-dialog
    header="Cancel truck plan"
    [(visible)]="cancelDialogVisible"
    [modal]="true"
    [style]="{ width: '36rem', maxWidth: '95vw' }"
    [draggable]="false">
    <div class="cancel-dialog">
      <p>Cancel {{ plan?.planNumber }} and its {{ plan?.routes?.length || 0 }} route(s). This releases the assigned drivers and trucks. The {{ plan?.releases?.length || 0 }} release(s) are kept and can be replanned if still open.</p>
      <label for="plan-cancellation-reason">Reason *</label>
      <textarea id="plan-cancellation-reason" [(ngModel)]="cancellationReason" rows="4" maxlength="500" placeholder="Explain why this plan is being cancelled"></textarea>
      @if (cancelDialogError) { <div class="cancel-dialog-error">{{ cancelDialogError }}</div> }
    </div>
    <ng-template pTemplate="footer">
      <p-button label="Keep Plan" severity="secondary" [outlined]="true" [disabled]="isLoading" (onClick)="cancelDialogVisible = false"></p-button>
      <p-button label="Cancel Plan" icon="pi pi-ban" severity="danger" [loading]="isLoading" [disabled]="!cancellationReason.trim()" (onClick)="cancelPlan()"></p-button>
    </ng-template>
  </p-dialog>
</app-page-shell>
